import { ComplexPersonCondition, ConditionalKeys, PersonFacilityPredicate, PersonLike, PersonMatcher, SimplePersonCondition, PersonCondition } from '@weavix/models/src/person/person-matcher';
import { isEmpty, isNil } from 'lodash';

// Danger: this logic is mirrored in mobile Android's channels/MatchPerson.kt.

export function conditionEvaluator(
    person: PersonLike,
    facilityLookup: PersonFacilityPredicate,
    facilityMatch = true,
    self?: PersonLike) {

    return (condition: SimplePersonCondition) => {
        if (!condition.value?.length && !condition.self) return null;

        // root level facility specification must match for any non-exact person match
        if (!facilityMatch && (condition.key !== ConditionalKeys.People || condition.negate)) return false;

        function evaluateStraight() {
            let value: string | string[] | undefined | PersonFacilityPredicate;
            let selfValue: string | string[] | undefined;
            switch (condition.key) {
                case ConditionalKeys.People:
                    value = person.id;
                    selfValue = self?.id;
                    break;
                case ConditionalKeys.PersonCompany:
                    value = person.companyId;
                    selfValue = self?.companyId;
                    break;
                case ConditionalKeys.PersonCraft:
                    value = person.crafts;
                    selfValue = self?.crafts;
                    break;
                case ConditionalKeys.PersonTags:
                    value = person.tags;
                    selfValue = self?.tags;
                    break;
                case ConditionalKeys.PersonSites:
                    value = (facilityId: string) => {
                    return facilityLookup(facilityId, person)
                        || person.facilityIds?.includes(facilityId)
                        || !!person.facilities?.[facilityId];
                    };
                    selfValue = self?.facilityIds ?? Object.keys(self?.facilities ?? {});
                    break;
            }
            let values = condition.value ?? [];
            if (condition.self) {
                if (!selfValue) values = [];
                else if (Array.isArray(selfValue)) values = selfValue;
                else values = [selfValue];
            }
            if (isNil(value)) return false;
            if (typeof value === 'function') return values.some(v => (value)(v));
            if (Array.isArray(value)) return (values.some(v => (value).includes(v)));
            return values.includes(value);
        }

        return evaluateStraight() ? !condition.negate : !!condition.negate;
    };
}

export function matchPerson(matcher: PersonMatcher, person: PersonLike, isPersonOnFacility: PersonFacilityPredicate = () => false, self?: PersonLike, falseOnNull?: boolean) {
    // Temporary support users should not be auto-added to channels.
    if (person.supportAccess) return false;
    if (self && !self.facilityIds && !self.facilities) {
        // developer error using this method without populating self
        throw new Error('Self matching requires facilityIds be populated');
    }

    function isFacilityMatch() {
        if (matcher.facilityIds && !isEmpty(matcher.facilityIds) && !matcher.facilityIds.some(facilityId => isPersonOnFacility(facilityId, person))) {
            return false;
        }
        if (matcher.excludedFacilityIds && !isEmpty(matcher.excludedFacilityIds) && matcher.excludedFacilityIds.some(facilityId => isPersonOnFacility(facilityId, person))) {
            return false;
        }
        return true;
    }

    const facilityMatch = isFacilityMatch();
    const evaluator = conditionEvaluator(person, isPersonOnFacility, facilityMatch, self);

    function checkMatch(condition: ComplexPersonCondition): boolean | null {
        let ret: boolean | null = null;
        const search = condition?.type === 'or';
        condition?.conditions?.forEach(v => {
            const evaluated = (v as SimplePersonCondition).key
                ? evaluator(v as SimplePersonCondition)
                : checkMatch(v as ComplexPersonCondition);
            if (evaluated === null) return;

            if (ret === null) ret = evaluated;
            else if (evaluated === search) ret = search;
        });
        return ret;
    }

    if (matcher.conditions) {
        const matchResult = checkMatch(matcher.conditions);
        if (matchResult === null && falseOnNull) return false;
        return matchResult ?? facilityMatch;
    }

    return facilityMatch;
}

export function collectConditionalKeys(condition: PersonCondition, key: ConditionalKeys, result = new Set<string>()): Set<string> {
    if (condition && 'conditions' in condition && Array.isArray((condition).conditions)) {
        const complexCondition = condition;
        for (const child of complexCondition.conditions) {
            collectConditionalKeys(child, key, result);
        }
    } else {
        const simpleCondition = condition as SimplePersonCondition;
        if (simpleCondition?.key === key && simpleCondition?.value) {
            for (const v of simpleCondition.value) {
                result.add(v);
            }
        }
    }
    return result;
}
